import { useSyncExternalStore } from 'react';

/**
 * 안내 투어가 <b>대신 열어 두는 탭</b>.
 *
 * <h2>왜 필요한가 (사용자 지시 2026-08-27)</h2>
 * 탭 줄을 밝히고 「여기 열 개가 목차입니다」 라고 말하는 안내는, 정작 <b>탭 안에서 무엇을 누르면
 * 무슨 일이 일어나는지</b> 를 끝내 설명하지 못한다. 그렇다고 사람에게 눌러 보라고 할 수도 없다 —
 * 투어 중에는 막이 클릭을 삼킨다({@link TourOverlay}).
 *
 * <p>그래서 걸음이 «이 탭을 열어 둬라» 를 여기 올려 두고, 화면이 그것을 자기 선택 위에 <b>얹어</b>
 * 읽는다({@link useTourOpenTab}). 주소(`?tab=`)는 건드리지 않으므로 안내가 끝나면 보던 탭으로
 * 그대로 돌아온다. 판을 대신 여는 {@code panels.ts}·사이드바를 대신 펼치는 {@code expanded.ts} 와
 * 같은 규율이고, 이유도 같다.
 *
 * <p>값은 <b>그 탭 버튼의 {@code data-tour}</b> 그대로 쓴다(`pmis-tab-progress`). 대본이 가리키는
 * 이름과 여는 이름이 같아야, 걸음 하나만 보고도 어느 탭에서 무엇을 밝히는지 읽힌다. 덤으로
 * 투어는 <b>그 버튼이 화면에 있을 때만</b> 그 걸음을 남길 수 있다 — 권한으로 빠진 탭을 설명하는
 * 일이 저절로 막힌다.
 */

let openTab: string | null = null;
const listeners = new Set<() => void>();

export function setTourOpenTab(next: string | null): void {
  // 같은 값으로 알리면 그 화면이 매 걸음 헛되이 다시 그려진다.
  if (next === openTab) return;

  openTab = next;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * 지금 걸음이 열어 두라고 한 탭. {@code prefix} 를 뗀 <b>탭 키</b>만 돌려준다.
 *
 * <p>화면마다 탭 이름이 겹치므로(둘 다 `overview` 일 수 있다) 접두어로 <b>어느 화면의 탭인가</b>를
 * 가른다 — 다른 화면의 걸음이 남긴 값에 엉뚱한 탭이 반응하지 않는다.
 */
export function useTourOpenTab(prefix: string): string | null {
  const key = useSyncExternalStore(
    subscribe,
    () => openTab,
    () => null,
  );

  return key && key.startsWith(prefix) ? key.slice(prefix.length) : null;
}
